import { forwardRef, useCallback, useMemo } from "react";
import { Modal, Button, Input, Select, Badge, Divider } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useMetadataStore } from "@ifa/utils";
import type { MemberSelectorProps, MemberSelectorRef } from "./types";
import {
  BUTTON_TEXTS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TITLE,
  SEARCH_PLACEHOLDER,
} from "./utils/constants";
import TeamList from "./TeamList";
import MemberList from "./MemberList";
import SelectedPanel from "./SelectedPanel";
import { useTeams } from "./hooks/useTeams";
import { useMemberSelectorState } from "./hooks/useMemberSelectorState";
import { getActiveMemberFilterCount } from "./utils/filters";
import { getPersonnelTypeOptions } from "./utils/personnelType";
import styles from "./MemberSelectorModal.module.css";

const MemberSelectorModal = forwardRef<MemberSelectorRef, MemberSelectorProps>(
  (
    {
      visible,
      onCancel,
      onConfirm,
      mode,
      maxCount,
      defaultValue,
      title = DEFAULT_TITLE,
      teams: propTeams,
      pageSize = DEFAULT_PAGE_SIZE,
      allowClear = true,
      confirmLoading = false,
      getContainer,
      zIndex,
      destroyOnHidden = true,
      disabledMemberIds,
      memberListFetcher,
      extraParams,
      defaultFilters,
    },
    ref
  ) => {
    const selectMetadataList = useMetadataStore(
      (state) => state.selectMetadataList
    );

    const {
      shouldRender,
      isOpen,
      selectedTeamId,
      inputValue,
      searchKeyword,
      filters,
      selectedMembers,
      selectedMemberIds,
      canConfirm,
      handleAfterOpenChange,
      handleSearch,
      handleTeamSelect,
      handleFilterChange,
      handleResetFilters,
      handleMemberSelect,
      handleMemberDeselect,
      handleClearAll,
    } = useMemberSelectorState({
      visible,
      defaultValue,
      mode,
      destroyOnHidden,
      defaultFilters,
      ref,
    });

    const disabledMemberIdsSet = useMemo(() => {
      return new Set(disabledMemberIds ?? []);
    }, [disabledMemberIds]);

    const { teams, loading: teamsLoading } = useTeams(propTeams, isOpen);

    const handleCancel = useCallback(() => {
      onCancel();
    }, [onCancel]);

    const handleConfirm = useCallback(() => {
      if (mode === "single") {
        onConfirm(selectedMembers[0] || null);
        return;
      }

      onConfirm(selectedMembers);
    }, [mode, onConfirm, selectedMembers]);

    const activeFilterCount = useMemo(() => {
      return getActiveMemberFilterCount({
        teamId: selectedTeamId,
        searchKeyword: inputValue,
        filters,
      });
    }, [filters, inputValue, selectedTeamId]);

    const personnelTypeOptions = useMemo(() => {
      return getPersonnelTypeOptions(selectMetadataList);
    }, [selectMetadataList]);

    const confirmText =
      mode === "multiple"
        ? BUTTON_TEXTS.CONFIRM_WITH_COUNT(selectedMembers.length)
        : BUTTON_TEXTS.CONFIRM;

    const selectedSummary =
      mode === "multiple"
        ? maxCount
          ? `${selectedMembers.length} / ${maxCount}`
          : `${selectedMembers.length} 人`
        : selectedMembers.length > 0
          ? "已選擇 1 人"
          : "未選擇";

    if (!shouldRender && destroyOnHidden) {
      return null;
    }

    return (
      <Modal
        title={
          <div className={styles.titleArea}>
            <div className={styles.headerTop}>
              <div className={styles.titleBlock}>
                <div className={styles.title}>{title}</div>
              </div>

              <div className={styles.summaryGroup}>
                <div
                  className={`${styles.summaryCard} ${styles.summaryCardPrimary}`}
                >
                  <div className={styles.summaryLabel}>已選成員</div>
                  <div className={styles.summaryValue}>{selectedSummary}</div>
                </div>

                
              </div>
            </div>

            <div className={styles.filterBar}>
              <div className={styles.filterControls}>
                <Input
                  placeholder={SEARCH_PLACEHOLDER}
                  prefix={<SearchOutlined className={styles.searchIcon} />}
                  allowClear
                  value={inputValue}
                  onChange={(event) => handleSearch(event.target.value)}
                  className={styles.searchInput}
                />

                <Select
                  allowClear
                  value={filters.personnelType}
                  placeholder="成員類型"
                  options={personnelTypeOptions}
                  onChange={(value) =>
                    handleFilterChange("personnelType", value)
                  }
                  className={styles.filterSelect}
                />

                {/* <Select
                  allowClear
                  value={filters.employmentStatus}
                  placeholder="在职状态"
                  options={EMPLOYMENT_STATUS_OPTIONS}
                  onChange={(value) =>
                    handleFilterChange("employmentStatus", value)
                  }
                  className={styles.filterSelect}
                />

                <Select
                  allowClear
                  value={filters.status}
                  placeholder="账号状态"
                  options={ACCOUNT_STATUS_OPTIONS}
                  onChange={(value) => handleFilterChange("status", value)}
                  className={styles.filterSelect}
                /> */}

                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilters}
                  disabled={activeFilterCount === 0}
                  className={styles.resetButton}
                >
                  {BUTTON_TEXTS.RESET_FILTERS}
                </Button>
              </div>
            </div>
          </div>
        }
        open={visible}
        afterOpenChange={handleAfterOpenChange}
        onCancel={handleCancel}
        closable={false}
        centered
        width={mode === "multiple" ? 1024 : 900}
        wrapClassName={styles.modalWrap}
        classNames={{
          header: styles.modalHeader,
          body: styles.modalBody,
          container: styles.modalContent,
          footer: styles.modalFooter,
        }}
        getContainer={getContainer}
        zIndex={zIndex}
        footer={
          <div className={styles.footerBar}>
            <div className={styles.footerMeta}>
              <Badge color="var(--theme-primary)" />
              <span className={styles.footerMetaStrong}>
                已選成員 {selectedMembers.length} 人
              </span>
              {maxCount ? (
                <>
                  <Divider type="vertical" className={styles.footerDivider} />
                  <span className={styles.footerMetaText}>上限 {maxCount} 人</span>
                </>
              ) : null}
            </div>

            <div className={styles.footerActions}>
              <Button onClick={handleCancel} className={styles.footerButton}>
                {BUTTON_TEXTS.CANCEL}
              </Button>
              <Button
                type="primary"
                onClick={handleConfirm}
                disabled={!canConfirm}
                loading={confirmLoading}
                className={styles.footerButton}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        }
      >
        <div className={styles.layout}>
          <div className={`${styles.panel} ${styles.teamPanel}`}>
            {isOpen ? (
              <TeamList
                teams={teams}
                loading={teamsLoading}
                selectedTeamId={selectedTeamId}
                onTeamSelect={handleTeamSelect}
              />
            ) : null}
          </div>

          <div className={`${styles.panel} ${styles.memberPanel}`}>
            {isOpen ? (
              <MemberList
                teamId={selectedTeamId}
                searchKeyword={searchKeyword}
                filters={filters}
                pageSize={pageSize}
                mode={mode}
                selectedMemberIds={selectedMemberIds}
                maxCount={maxCount}
                disabledMemberIds={disabledMemberIdsSet}
                extraParams={extraParams}
                memberListFetcher={memberListFetcher}
                onMemberSelect={handleMemberSelect}
                onMemberDeselect={handleMemberDeselect}
              />
            ) : null}
          </div>

          {mode === "multiple" ? (
            <div className={`${styles.panel} ${styles.selectedPanel}`}>
              <SelectedPanel
                members={selectedMembers}
                showClearAction={allowClear}
                onRemove={handleMemberDeselect}
                onClearAll={handleClearAll}
              />
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }
);

MemberSelectorModal.displayName = "MemberSelectorModal";

export default MemberSelectorModal;
